import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

// ── useThirdPartyRatings ─────────────────────────────────────────────────────
// IMDb / Rotten Tomatoes / Metacritic for a show, from the cached OMDb fetch
// (omdb_ratings, world-readable). Mirrors useEpisodeViewership.js's shape: read
// the cache first (fast, no function round-trip on a hit), only call the
// fetch function if this show has never been attempted.
//
// `passive` (added when this got reused on ShowCard, 2026-09-17): grids can
// render dozens of cards at once, and each is a distinct showId — without an
// opt-out every uncached show in a Discover/Home grid would fire its own
// omdb-ratings call in parallel, which is both a self-inflicted request storm
// and a fast way to burn OMDb's free-tier daily quota on shows nobody is even
// looking at yet. Passive callers read the cache once and stop there; only the
// show-detail page (one show, a real "someone is looking at this" signal)
// triggers the actual fetch-on-miss.
export function useThirdPartyRatings(showId, { passive = false } = {}) {
  const [row, setRow] = useState(null)   // { imdb_rating, rt_rating, metacritic_rating } | null

  useEffect(() => {
    if (!showId) { setRow(null); return }
    let cancel = false

    const readCache = async () => {
      const { data } = await supabase.from('omdb_ratings')
        .select('imdb_rating, rt_rating, metacritic_rating').eq('show_id', Number(showId)).maybeSingle()
      if (cancel) return false
      setRow(data || null)
      return !!data
    }

    ;(async () => {
      const have = await readCache()
      if (cancel || have || passive) return
      try {
        await fetch(`/.netlify/functions/omdb-ratings?show_id=${showId}`)
        if (!cancel) await readCache()
      } catch { /* best-effort */ }
    })()

    return () => { cancel = true }
  }, [showId, passive])

  const hasAny = !!(row && (row.imdb_rating || row.rt_rating || row.metacritic_rating))
  return { imdb: row?.imdb_rating || null, rt: row?.rt_rating || null, metacritic: row?.metacritic_rating || null, hasAny }
}
